export type OrderBookDepthBand = {
  pct: number;
  bid_notional: number;
  ask_notional: number;
  covered?: boolean;
  ratio?: number | null;
};

export type OrderBookDepthCoverage = {
  bid_pct: number;
  ask_pct: number;
};

export type OrderBookDepthSample = {
  askNotionals?: Record<number, number | null>;
  bidNotionals?: Record<number, number | null>;
  collectedAt?: string;
  label: string;
  price?: number | null;
  ratios: Record<number, number | null>;
  values: Record<number, number | null>;
};

export type OrderBookDepthHistoryPoint = {
  time: number;
  bands: OrderBookDepthBand[];
};

export type OrderBookPriceBar = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
};

export type OrderBookChartRow = {
  time: number;
  collectedAt: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
  depth5: number | null;
  depth10: number | null;
};

export const DEPTH_TIMEFRAMES = ["4h", "12h", "1d", "1w"] as const;

export type DepthTimeframe = (typeof DEPTH_TIMEFRAMES)[number];

export type DepthSignalState = "strong-bid" | "bid-fade" | "neutral-fade" | "ask-fade" | "strong-ask";

export function depthHistoryLimitForRenderedSeries(renderedBars = 500) {
  return Math.max(120, Math.min(1000, Math.floor(renderedBars)));
}

function roundTo(value: number, decimals: number) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function depthImbalance(band: Pick<OrderBookDepthBand, "bid_notional" | "ask_notional">) {
  const total = band.bid_notional + band.ask_notional;
  if (!total) return null;
  return roundTo(((band.bid_notional - band.ask_notional) / total) * 100, 2);
}

export function depthImbalanceForBand(
  sample: Pick<OrderBookDepthSample, "bidNotionals" | "askNotionals">,
  pct: number
) {
  const bidNotional = sample.bidNotionals?.[pct];
  const askNotional = sample.askNotionals?.[pct];
  if (typeof bidNotional !== "number" || typeof askNotional !== "number") return null;
  return depthImbalance({ bid_notional: bidNotional, ask_notional: askNotional });
}

export function depthCoverageWarning(band: Pick<OrderBookDepthBand, "pct" | "covered">, coverage?: OrderBookDepthCoverage | null) {
  if (band.covered !== false || !coverage) return "";
  return `Partial Binance book: ${band.pct}% requested, REST returned ${roundTo(coverage.bid_pct, 2)}% bid / ${roundTo(coverage.ask_pct, 2)}% ask.`;
}

export function formatDepthBandLabel(pct: number, market: "spot" | "perp") {
  const marketLabel = market === "spot" ? "Spot" : "Perp";
  return `Order Book Depth 0% ${pct}% ${marketLabel} Binance USDT`;
}

export function nextDepthSamples(
  previous: OrderBookDepthSample[],
  bands: OrderBookDepthBand[],
  label: string,
  limit = 40,
  meta: { collectedAt?: string; price?: number | null } = {}
) {
  const values: Record<number, number | null> = {};
  const ratios: Record<number, number | null> = {};
  const bidNotionals: Record<number, number | null> = {};
  const askNotionals: Record<number, number | null> = {};
  for (const band of bands) {
    values[band.pct] = depthImbalance(band);
    ratios[band.pct] = band.ratio ?? null;
    bidNotionals[band.pct] = band.bid_notional;
    askNotionals[band.pct] = band.ask_notional;
  }
  return [...previous, { askNotionals, bidNotionals, collectedAt: meta.collectedAt, label, price: meta.price ?? null, ratios, values }].slice(-limit);
}

export function depthHistorySamples(history: OrderBookDepthHistoryPoint[], requiredBands: number[] = [5, 10]) {
  return history
    .filter(
      (point) =>
        Number.isFinite(point.time) &&
        point.time > 0 &&
        requiredBands.every((pct) => point.bands.some((band) => band.pct === pct))
    )
    .map((point) => {
      const collectedAt = new Date(point.time).toISOString();
      const label = `${collectedAt.slice(5, 10)} ${collectedAt.slice(11, 16)}`;
      return nextDepthSamples([], point.bands, label, 1, { collectedAt, price: null })[0];
    });
}

export function mergeDepthSamples(
  previous: OrderBookDepthSample[],
  incoming: OrderBookDepthSample[],
  limit = 500
) {
  const byTimestamp = new Map<string, OrderBookDepthSample>();
  for (const sample of [...previous, ...incoming]) {
    if (!sample.collectedAt || !Number.isFinite(Date.parse(sample.collectedAt))) continue;
    byTimestamp.set(sample.collectedAt, sample);
  }
  return [...byTimestamp.values()]
    .sort((left, right) => Date.parse(left.collectedAt ?? "") - Date.parse(right.collectedAt ?? ""))
    .slice(-Math.max(1, Math.floor(limit)));
}

function epochMilliseconds(value: number) {
  return value < 10_000_000_000 ? value * 1000 : value;
}

export function orderBookChartRows(
  samples: OrderBookDepthSample[],
  bars: OrderBookPriceBar[],
  timeframe: DepthTimeframe = "4h"
) {
  const bucketMs = depthTimeframeMs(timeframe);
  const rows = new Map<number, OrderBookChartRow>();
  const rowFor = (time: number) => {
    const bucket = Math.floor(time / bucketMs) * bucketMs;
    const existing = rows.get(bucket);
    if (existing) return existing;
    const row: OrderBookChartRow = {
      time: bucket,
      collectedAt: new Date(bucket).toISOString(),
      open: null,
      high: null,
      low: null,
      close: null,
      volume: null,
      depth5: null,
      depth10: null
    };
    rows.set(bucket, row);
    return row;
  };

  for (const bar of bars) {
    const time = epochMilliseconds(bar.time);
    if (!Number.isFinite(time)) continue;
    const row = rowFor(time);
    row.open = Number.isFinite(bar.open) ? bar.open : null;
    row.high = Number.isFinite(bar.high) ? bar.high : null;
    row.low = Number.isFinite(bar.low) ? bar.low : null;
    row.close = Number.isFinite(bar.close) ? bar.close : null;
    row.volume = bar.volume != null && Number.isFinite(bar.volume) ? bar.volume : null;
  }

  for (const sample of samples) {
    const time = sample.collectedAt ? Date.parse(sample.collectedAt) : Number.NaN;
    if (!Number.isFinite(time)) continue;
    const row = rowFor(time);
    row.depth5 = depthImbalanceForBand(sample, 5) ?? sample.values[5] ?? null;
    row.depth10 = depthImbalanceForBand(sample, 10) ?? sample.values[10] ?? null;
  }

  return [...rows.values()]
    .filter((row) => row.close != null || row.depth5 != null || row.depth10 != null)
    .sort((left, right) => left.time - right.time);
}

export function initialDepthSampleDelays(count: number, intervalMs: number) {
  const safeCount = Math.max(0, Math.floor(count));
  const safeInterval = Math.max(0, Math.floor(intervalMs));
  return Array.from({ length: safeCount }, (_, index) => index * safeInterval);
}

export function depthTimeframeMs(timeframe: DepthTimeframe) {
  switch (timeframe) {
    case "4h":
      return 4 * 60 * 60 * 1000;
    case "12h":
      return 12 * 60 * 60 * 1000;
    case "1d":
      return 24 * 60 * 60 * 1000;
    case "1w":
      return 7 * 24 * 60 * 60 * 1000;
  }
}

function formatDepthBucketLabel(bucketStartMs: number, timeframe: DepthTimeframe) {
  const date = new Date(bucketStartMs);
  const options: Intl.DateTimeFormatOptions = timeframe === "1d" || timeframe === "1w"
    ? { year: "numeric", month: "2-digit", day: "2-digit" }
    : { month: "2-digit", day: "2-digit", hour: "2-digit" };
  return date.toLocaleString([], options);
}

export function bucketDepthSamples(samples: OrderBookDepthSample[], timeframe: DepthTimeframe) {
  const bucketMs = depthTimeframeMs(timeframe);
  const byBucket = new Map<number, OrderBookDepthSample>();

  for (const sample of samples) {
    const timestamp = sample.collectedAt ? Date.parse(sample.collectedAt) : Number.NaN;
    if (!Number.isFinite(timestamp)) continue;
    const bucketStartMs = Math.floor(timestamp / bucketMs) * bucketMs;
    byBucket.set(bucketStartMs, {
      ...sample,
      label: formatDepthBucketLabel(bucketStartMs, timeframe)
    });
  }

  const bucketed = [...byBucket.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, sample]) => sample);

  return bucketed.length >= 2 ? bucketed : samples;
}

export function depthSampleX(index: number, count: number, padding = 2) {
  const safeCount = Math.max(1, Math.floor(count));
  if (safeCount === 1) return 50;
  const safePadding = Math.max(0, Math.min(20, padding));
  const clampedIndex = Math.max(0, Math.min(Math.floor(index), safeCount - 1));
  return roundTo(safePadding + (clampedIndex / (safeCount - 1)) * (100 - safePadding * 2), 4);
}

export function depthBarRect(imbalance: number, index: number, count: number) {
  const capped = Math.max(-60, Math.min(60, imbalance));
  const height = roundTo(Math.max(2, (Math.abs(capped) / 60) * 42), 4);
  const width = roundTo(Math.max(0.8, Math.min(2.4, 72 / Math.max(1, count))), 4);
  const centerX = depthSampleX(index, count);
  const x = roundTo(Math.max(0, Math.min(100 - width, centerX - width / 2)), 4);
  const positive = capped >= 0;
  return {
    height,
    positive,
    width,
    x,
    y: roundTo(positive ? 50 - height : 50, 4)
  };
}

export function depthSignalState(imbalance: number | null | undefined): DepthSignalState {
  if (imbalance == null || !Number.isFinite(imbalance)) return "neutral-fade";
  if (imbalance >= 70) return "strong-bid";
  if (imbalance <= -40) return "strong-ask";
  if (imbalance > 0) return "bid-fade";
  if (imbalance < 0) return "ask-fade";
  return "neutral-fade";
}

export function depthSignalColor(state: DepthSignalState) {
  switch (state) {
    case "strong-bid":
      return "#22c55e";
    case "bid-fade":
      return "#38bdf8";
    case "strong-ask":
      return "#ef4444";
    case "ask-fade":
      return "#fb7185";
    default:
      return "#94a3b8";
  }
}

export function depthSignalOpacity(state: DepthSignalState) {
  return state === "strong-bid" || state === "strong-ask" ? 1 : 0.42;
}
